import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InventoryManagementEnhanced = () => {
  const theme = useThemeClasses();
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'materials'
  const [inventoryData, setInventoryData] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState({ low_stock: [], needs_reorder: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  
  const [materialForm, setMaterialForm] = useState({
    name: '',
    description: '',
    category: 'Raw Material',
    unit_of_measure: 'units',
    stock_quantity: 0,
    weight: null,
    weight_unit: 'g',
    low_stock_threshold: 5,
    reorder_level: 10,
    pricing_type: 'per_unit',
    cost_per_unit: 0,
    purchase_quantity: null,
    purchase_price: null,
    supplier: '',
    supplier_sku: '',
    location: '',
    color: '',
    notes: ''
  });

  useEffect(() => {
    loadInventoryData();
  }, []);

  useEffect(() => {
    if (activeTab === 'materials') {
      loadLowStockAlerts();
    }
  }, [activeTab, materials]);

  const loadInventoryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventoryData(response.data);
      setMaterials(response.data.materials || []);
    } catch (error) {
      console.error('Failed to load inventory:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const loadLowStockAlerts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/materials/low-stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLowStockAlerts(response.data);
    } catch (error) {
      console.error('Failed to load low stock alerts:', error);
    }
  };

  const handleQuickAdjust = async (itemId, change, itemType = 'product') => {
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = itemType === 'product' 
        ? `${API}/admin/inventory/${itemId}/adjust`
        : `${API}/admin/materials/${itemId}/adjust`;
      
      await axios.post(endpoint, {
        type: change > 0 ? 'add' : 'subtract',
        quantity: Math.abs(change),
        reason: `Quick adjustment: ${change > 0 ? '+' : ''}${change}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Stock ${change > 0 ? 'increased' : 'decreased'} by ${Math.abs(change)}`);
      loadInventoryData();
    } catch (error) {
      console.error('Failed to adjust inventory:', error);
      toast.error('Failed to adjust inventory');
    }
  };

  const handleSaveMaterial = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (editingMaterial) {
        // Update existing material
        await axios.put(`${API}/admin/materials/${editingMaterial.id}`, materialForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Material updated successfully');
      } else {
        // Create new material
        await axios.post(`${API}/admin/materials`, materialForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Material created successfully');
      }
      
      setShowAddMaterial(false);
      setEditingMaterial(null);
      setMaterialForm({
        name: '',
        description: '',
        category: 'Raw Material',
        unit_of_measure: 'units',
        stock_quantity: 0,
        weight: null,
        weight_unit: 'g',
        low_stock_threshold: 5,
        reorder_level: 10,
        pricing_type: 'per_unit',
        cost_per_unit: 0,
        purchase_quantity: null,
        purchase_price: null,
        supplier: '',
        supplier_sku: '',
        location: '',
        color: '',
        notes: ''
      });
      loadInventoryData();
    } catch (error) {
      console.error('Failed to save material:', error);
      toast.error('Failed to save material');
    }
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setMaterialForm({
      name: material.name || '',
      description: material.description || '',
      category: material.category || 'Raw Material',
      unit_of_measure: material.unit_of_measure || 'units',
      stock_quantity: material.stock_quantity || 0,
      weight: material.weight || null,
      weight_unit: material.weight_unit || 'g',
      low_stock_threshold: material.low_stock_threshold || 5,
      reorder_level: material.reorder_level || 10,
      pricing_type: material.pricing_type || 'per_unit',
      cost_per_unit: material.cost_per_unit || 0,
      purchase_quantity: material.purchase_quantity || null,
      purchase_price: material.purchase_price || null,
      supplier: material.supplier || '',
      supplier_sku: material.supplier_sku || '',
      location: material.location || '',
      color: material.color || '',
      notes: material.notes || ''
    });
    setShowAddMaterial(true);
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/admin/materials/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material deleted successfully');
      loadInventoryData();
    } catch (error) {
      console.error('Failed to delete material:', error);
      toast.error('Failed to delete material');
    }
  };

  const handleEditProduct = (product) => {
    // Store product ID and trigger page reload to Products tab
    sessionStorage.setItem('editProductId', product.id);
    sessionStorage.setItem('switchToProductsTab', 'true');
    window.location.reload();
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      loadInventoryData();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleQuickStockAdjust = async (productId, amount) => {
    try {
      const token = localStorage.getItem('access_token');
      const product = inventoryData.products.find(p => p.id === productId);
      
      if (!product || !product.track_inventory || product.is_made_to_order) {
        toast.error('Cannot adjust stock for this product');
        return;
      }

      const newQuantity = Math.max(0, product.stock_quantity + amount);
      
      await axios.put(`${API}/admin/products/${productId}`, {
        ...product,
        stock_quantity: newQuantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Stock ${amount > 0 ? 'increased' : 'decreased'} successfully`);
      loadInventoryData();
    } catch (error) {
      console.error('Failed to adjust stock:', error);
      toast.error('Failed to adjust stock');
    }
  };


  const getFilteredItems = () => {
    const items = activeTab === 'products' 
      ? inventoryData?.products || []
      : materials;
    
    let filtered = items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (filterStatus === 'low') {
      filtered = filtered.filter(item => item.is_low_stock);
    } else if (filterStatus === 'out') {
      filtered = filtered.filter(item => item.is_out_of_stock);
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={theme.textSecondary}>Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>📦 Inventory Management</h2>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('products')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'products'
                ? 'border-blue-500 text-blue-600'
                : `border-transparent ${theme.textSecondary} hover:text-blue-600`
            }`}
          >
            🛍️ Products ({inventoryData?.summary?.total_products || 0})
          </button>
          <button
            onClick={() => setActiveTab('materials')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'materials'
                ? 'border-blue-500 text-blue-600'
                : `border-transparent ${theme.textSecondary} hover:text-blue-600`
            }`}
          >
            🧰 Materials & Supplies ({inventoryData?.summary?.total_materials || 0})
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {activeTab === 'products' ? (
          <>
            <div className={`${theme.isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'} p-4 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-blue-400' : 'text-blue-600'}`}>Total Products</p>
              <p className={`text-2xl font-bold ${theme.isDark ? 'text-blue-300' : 'text-blue-800'}`}>{inventoryData?.summary?.total_products || 0}</p>
            </div>
            <div className={`${theme.isDark ? 'bg-yellow-900/30 border-yellow-700' : 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200'} p-4 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>Low Stock</p>
              <p className={`text-2xl font-bold ${theme.isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>{inventoryData?.summary?.low_stock_products || 0}</p>
            </div>
            <div className={`${theme.isDark ? 'bg-red-900/30 border-red-700' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'} p-4 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-red-400' : 'text-red-600'}`}>Out of Stock</p>
              <p className={`text-2xl font-bold ${theme.isDark ? 'text-red-300' : 'text-red-800'}`}>{inventoryData?.summary?.out_of_stock_products || 0}</p>
            </div>
            <div className={`${theme.isDark ? 'bg-green-900/30 border-green-700' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} p-4 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>Total Value</p>
              <p className={`text-2xl font-bold ${theme.isDark ? 'text-green-300' : 'text-green-800'}`}>${(inventoryData?.summary?.total_product_value || 0).toFixed(2)}</p>
            </div>
          </>
        ) : (
          <>
            <div className={`${theme.isDark ? 'bg-purple-900/30 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'} p-4 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-purple-400' : 'text-purple-600'}`}>Total Materials</p>
              <p className={`text-2xl font-bold ${theme.isDark ? 'text-purple-300' : 'text-purple-800'}`}>{inventoryData?.summary?.total_materials || 0}</p>
            </div>
            <div className={`${theme.isDark ? 'bg-yellow-900/30 border-yellow-700' : 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200'} p-4 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>Low Stock Materials</p>
              <p className={`text-2xl font-bold ${theme.isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>{inventoryData?.summary?.low_stock_materials || 0}</p>
            </div>
            <div className={`${theme.isDark ? 'bg-green-900/30 border-green-700' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} p-4 rounded-lg border`}>
              <p className={`text-sm font-medium mb-1 ${theme.isDark ? 'text-green-400' : 'text-green-600'}`}>Total Value</p>
              <p className={`text-2xl font-bold ${theme.isDark ? 'text-green-300' : 'text-green-800'}`}>${(inventoryData?.summary?.total_material_value || 0).toFixed(2)}</p>
            </div>
            <div className={`${theme.card} p-4 rounded-lg border ${theme.border}`}>
              <button
                onClick={() => {
                  setEditingMaterial(null);
                  setShowAddMaterial(true);
                }}
                className="w-full h-full flex flex-col items-center justify-center text-blue-600 hover:text-blue-700"
                data-testid="add-material-button"
              >
                <span className="text-3xl mb-1">+</span>
                <span className="text-sm font-medium">Add Material</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`flex-1 px-4 py-2 rounded-lg ${theme.input}`}
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={`px-4 py-2 rounded-lg ${theme.input}`}
        >
          <option value="all">All Items</option>
          <option value="low">Low Stock</option>
          {activeTab === 'products' && <option value="out">Out of Stock</option>}
        </select>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'products' && (
        <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={theme.tableHeader}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Product</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Category</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Stock</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Status</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`${theme.bgPrimary} ${theme.tableBorder}`}>
              {getFilteredItems().map((product) => (
                <tr key={product.id} className={theme.tableRow}>
                  <td className="px-6 py-4">
                    <div>
                      <div className={`font-medium ${theme.textPrimary}`}>{product.name}</div>
                      <div className={`text-sm ${theme.textSecondary}`}>${product.price?.toFixed(2) || '0.00'}</div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.textSecondary}`}>{product.category || 'Uncategorized'}</td>
                  <td className="px-6 py-4">
                    {product.is_made_to_order ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 font-medium">
                            ✨ Made to Order
                          </span>
                        </div>
                        <div className={`text-xs mt-1 ${theme.textSecondary}`}>
                          Unlimited supply available
                        </div>
                      </div>
                    ) : product.track_inventory ? (
                      <div>
                        <span className={`text-sm font-medium ${theme.textPrimary}`}>
                          {product.stock_quantity} / {product.low_stock_threshold}
                        </span>
                        <div className={`text-xs mt-1 ${theme.textSecondary}`}>
                          Current / Alert threshold
                        </div>
                      </div>
                    ) : (
                      <span className={`text-sm ${theme.textSecondary}`}>Not tracked</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {product.is_made_to_order ? (
                      <span className={`px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400`}>
                        Always Available
                      </span>
                    ) : product.track_inventory ? (
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        product.is_out_of_stock 
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          : product.is_low_stock
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      }`}>
                        {product.is_out_of_stock ? 'Out of Stock' : product.is_low_stock ? 'Low Stock' : 'In Stock'}
                      </span>
                    ) : (
                      <span className={`px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`}>
                        Not Tracked
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditProduct(product)}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                      {product.track_inventory && !product.is_made_to_order && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleQuickStockAdjust(product.id, -1)}
                            className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                            title="Decrease stock by 1"
                          >
                            -1
                          </button>
                          <button
                            onClick={() => handleQuickStockAdjust(product.id, 1)}
                            className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                            title="Increase stock by 1"
                          >
                            +1
                          </button>
                          <button
                            onClick={() => handleQuickStockAdjust(product.id, 5)}
                            className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                            title="Increase stock by 5"
                          >
                            +5
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {activeTab === 'materials' && (
        <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={theme.tableHeader}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Material</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Category</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Stock</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Unit</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Color</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Supplier</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`${theme.bgPrimary} ${theme.tableBorder}`}>
              {getFilteredItems().map((material) => (
                <tr key={material.id} className={theme.tableRow}>
                  <td className="px-6 py-4">
                    <div>
                      <div className={`font-medium ${theme.textPrimary}`}>{material.name}</div>
                      <div className={`text-sm ${theme.textSecondary}`}>
                        ${material.cost_per_unit?.toFixed(2) || '0.00'} per {material.unit_of_measure}
                        {material.weight && (
                          <span className="ml-2">({material.weight} {material.weight_unit})</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.textSecondary}`}>{material.category}</td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center gap-2">
                        {material.pricing_type === 'bulk_purchase' && material.purchase_quantity ? (
                          <div className="flex flex-col">
                            <span className={`text-lg font-bold text-blue-600 dark:text-blue-400`}>
                              {((material.stock_quantity || 0) * (material.purchase_quantity || 1)).toFixed(0)} {material.unit_of_measure}
                            </span>
                            <span className={`text-xs ${theme.textSecondary} mt-1`}>
                              ({material.stock_quantity} packs × {material.purchase_quantity})
                            </span>
                          </div>
                        ) : (
                          <span className={`text-sm font-medium ${theme.textPrimary}`}>
                            {material.stock_quantity} {material.unit_of_measure}
                          </span>
                        )}
                        {material.stock_quantity <= material.low_stock_threshold && (
                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                            Low
                          </span>
                        )}
                        {material.stock_quantity <= material.reorder_level && (
                          <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                            Reorder
                          </span>
                        )}
                      </div>
                      <div className={`text-xs mt-1 ${theme.textSecondary}`}>
                        Threshold: {material.low_stock_threshold} | Reorder: {material.reorder_level}
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.textSecondary}`}>{material.unit_of_measure}</td>
                  <td className={`px-6 py-4 text-sm`}>
                    {material.color ? (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${theme.textPrimary} bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30`}>
                        🎨 {material.color}
                      </span>
                    ) : (
                      <span className={theme.textSecondary}>-</span>
                    )}
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.textSecondary}`}>{material.supplier || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditMaterial(material)}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteMaterial(material.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      >
                        Delete
                      </button>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleQuickAdjust(material.id, -1, 'material')}
                          className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                        >
                          -1
                        </button>
                        <button
                          onClick={() => handleQuickAdjust(material.id, 1, 'material')}
                          className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                        >
                          +1
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Enhanced Add/Edit Material Modal with 5 Organized Sections */}
      {showAddMaterial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.card} rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
            {/* Sticky Header */}
            <div className={`sticky top-0 ${theme.card} border-b ${theme.border} px-6 py-4 flex justify-between items-center z-10`}>
              <div>
                <h3 className={`text-2xl font-bold ${theme.textPrimary}`}>
                  {editingMaterial ? '✏️ Edit Material' : '➕ Add New Material'}
                </h3>
                <p className={`text-sm ${theme.textSecondary} mt-1`}>
                  {editingMaterial ? 'Update material information' : 'Add a new material to your inventory'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAddMaterial(false);
                  setEditingMaterial(null);
                }}
                className={`text-2xl ${theme.textSecondary} hover:${theme.textPrimary} transition-colors`}
                data-testid="close-modal-button"
              >
                ×
              </button>
            </div>

            {/* Form Content with 5 Organized Sections */}
            <div className="px-6 py-6 space-y-6">
              {/* Section 1: Basic Information */}
              <div className={`${theme.isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-lg p-5 border`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">1</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className={`text-lg font-semibold ${theme.textPrimary}`}>Basic Information</h4>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                      Material Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={materialForm.name}
                      onChange={(e) => setMaterialForm({...materialForm, name: e.target.value})}
                      className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="e.g., PLA Filament, Resin, Paint"
                      required
                      data-testid="material-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Description</label>
                    <textarea
                      value={materialForm.description}
                      onChange={(e) => setMaterialForm({...materialForm, description: e.target.value})}
                      className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      rows={3}
                      placeholder="Brief description of the material..."
                      data-testid="material-description-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Category</label>
                      <select
                        value={materialForm.category}
                        onChange={(e) => setMaterialForm({...materialForm, category: e.target.value})}
                        className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                        data-testid="material-category-select"
                      >
                        <option value="Raw Material">Raw Material</option>
                        <option value="Supplies">Supplies</option>
                        <option value="Tools">Tools</option>
                        <option value="Packaging">Packaging</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Unit of Measure</label>
                      <select
                        value={materialForm.unit_of_measure}
                        onChange={(e) => setMaterialForm({...materialForm, unit_of_measure: e.target.value})}
                        className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                        data-testid="material-unit-select"
                      >
                        <option value="units">Units</option>
                        <option value="kg">Kilograms</option>
                        <option value="g">Grams</option>
                        <option value="liters">Liters</option>
                        <option value="ml">Milliliters</option>
                        <option value="meters">Meters</option>
                        <option value="cm">Centimeters</option>
                        <option value="rolls">Rolls</option>
                        <option value="sheets">Sheets</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Stock & Inventory Levels */}
              <div className={`${theme.isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} rounded-lg p-5 border`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                    <span className="text-green-600 dark:text-green-400 font-bold text-sm">2</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h4 className={`text-lg font-semibold ${theme.textPrimary}`}>Stock & Inventory Levels</h4>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Current Stock</label>
                    <input
                      type="number"
                      value={materialForm.stock_quantity}
                      onChange={(e) => setMaterialForm({...materialForm, stock_quantity: parseFloat(e.target.value) || 0})}
                      className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      data-testid="current-stock-input"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                      Low Stock Alert
                      <span className={`ml-1 text-xs ${theme.textSecondary}`}>⚠️</span>
                    </label>
                    <input
                      type="number"
                      value={materialForm.low_stock_threshold}
                      onChange={(e) => setMaterialForm({...materialForm, low_stock_threshold: parseFloat(e.target.value) || 0})}
                      className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      min="0"
                      step="0.01"
                      placeholder="5"
                      data-testid="low-stock-alert-input"
                    />
                    <p className={`text-xs mt-1 ${theme.textSecondary}`}>Alert below this level</p>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                      Reorder Point
                      <span className={`ml-1 text-xs ${theme.textSecondary}`}>🔄</span>
                    </label>
                    <input
                      type="number"
                      value={materialForm.reorder_level}
                      onChange={(e) => setMaterialForm({...materialForm, reorder_level: parseFloat(e.target.value) || 0})}
                      className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      min="0"
                      step="0.01"
                      placeholder="10"
                      data-testid="reorder-point-input"
                    />
                    <p className={`text-xs mt-1 ${theme.textSecondary}`}>Recommended stock level</p>
                  </div>
                </div>
              </div>

              {/* Section 3: Weight & Cost Analysis */}
              <div className={`${theme.isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'} rounded-lg p-5 border`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                    <span className="text-purple-600 dark:text-purple-400 font-bold text-sm">3</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <h4 className={`text-lg font-semibold ${theme.textPrimary}`}>Weight & Cost Analysis</h4>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Pricing Type Selector */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Pricing Method</label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="pricing_type"
                          value="per_unit"
                          checked={materialForm.pricing_type === 'per_unit'}
                          onChange={(e) => setMaterialForm({...materialForm, pricing_type: e.target.value, purchase_quantity: null, purchase_price: null})}
                          className="mr-2"
                        />
                        <span className={theme.textPrimary}>Per Unit Price</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="pricing_type"
                          value="bulk_purchase"
                          checked={materialForm.pricing_type === 'bulk_purchase'}
                          onChange={(e) => setMaterialForm({...materialForm, pricing_type: e.target.value})}
                          className="mr-2"
                        />
                        <span className={theme.textPrimary}>Bulk Purchase (Pack/Bundle)</span>
                      </label>
                    </div>
                  </div>

                  {/* Per Unit Pricing */}
                  {materialForm.pricing_type === 'per_unit' && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Cost Per Unit ($)</label>
                        <input
                          type="number"
                          value={materialForm.cost_per_unit}
                          onChange={(e) => setMaterialForm({...materialForm, cost_per_unit: parseFloat(e.target.value) || 0})}
                          className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="cost-per-unit-input"
                        />
                      </div>
                      
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                          Weight Per Unit <span className={`text-xs ${theme.textSecondary}`}>(Optional)</span>
                        </label>
                        <input
                          type="number"
                          value={materialForm.weight || ''}
                          onChange={(e) => setMaterialForm({...materialForm, weight: e.target.value ? parseFloat(e.target.value) : null})}
                          className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                          min="0"
                          step="0.01"
                          placeholder="1000"
                          data-testid="weight-per-unit-input"
                        />
                      </div>
                      
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Weight Unit</label>
                        <select
                          value={materialForm.weight_unit}
                          onChange={(e) => setMaterialForm({...materialForm, weight_unit: e.target.value})}
                          className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                          data-testid="weight-unit-select"
                        >
                          <option value="g">Grams (g)</option>
                          <option value="kg">Kilograms (kg)</option>
                          <option value="lb">Pounds (lb)</option>
                          <option value="oz">Ounces (oz)</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Bulk Purchase Pricing */}
                  {materialForm.pricing_type === 'bulk_purchase' && (
                    <>
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className={`text-sm font-medium ${theme.textPrimary}`}>Bulk Purchase Mode</span>
                        </div>
                        <p className={`text-xs ${theme.textSecondary}`}>
                          Enter how many pieces come in a pack and the total price. Cost per piece will be calculated automatically.
                        </p>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Pieces in Pack/Bundle</label>
                          <input
                            type="number"
                            value={materialForm.purchase_quantity || ''}
                            onChange={(e) => {
                              const qty = parseFloat(e.target.value) || 0;
                              const price = materialForm.purchase_price || 0;
                              const costPerUnit = qty > 0 ? price / qty : 0;
                              setMaterialForm({
                                ...materialForm,
                                purchase_quantity: qty,
                                cost_per_unit: costPerUnit
                              });
                            }}
                            className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                            min="0"
                            step="1"
                            placeholder="100"
                          />
                          <p className={`text-xs mt-1 ${theme.textSecondary}`}>e.g., 100 hooks</p>
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Total Pack Price ($)</label>
                          <input
                            type="number"
                            value={materialForm.purchase_price || ''}
                            onChange={(e) => {
                              const price = parseFloat(e.target.value) || 0;
                              const qty = materialForm.purchase_quantity || 0;
                              const costPerUnit = qty > 0 ? price / qty : 0;
                              setMaterialForm({
                                ...materialForm,
                                purchase_price: price,
                                cost_per_unit: costPerUnit
                              });
                            }}
                            className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                            min="0"
                            step="0.01"
                            placeholder="20.00"
                          />
                          <p className={`text-xs mt-1 ${theme.textSecondary}`}>e.g., $20 for pack</p>
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Cost Per Piece</label>
                          <input
                            type="text"
                            value={`$${materialForm.cost_per_unit?.toFixed(4) || '0.0000'}`}
                            disabled
                            className={`w-full px-4 py-3 rounded-lg ${theme.input} bg-emerald-50 dark:bg-emerald-900/20 cursor-not-allowed font-bold text-emerald-600 dark:text-emerald-400`}
                          />
                          <p className={`text-xs mt-1 ${theme.textSecondary}`}>Auto-calculated</p>
                        </div>
                      </div>

                      {materialForm.purchase_quantity > 0 && materialForm.purchase_price > 0 && (
                        <div className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-lg p-4 border-2 border-emerald-200 dark:border-emerald-800">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className={`text-sm font-semibold ${theme.textPrimary}`}>Calculation Summary</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            <div>
                              <span className={theme.textSecondary}>Pack of:</span>{' '}
                              <span className={`font-bold ${theme.textPrimary}`}>{materialForm.purchase_quantity} pieces</span>
                            </div>
                            <div>
                              <span className={theme.textSecondary}>Pack price:</span>{' '}
                              <span className={`font-bold ${theme.textPrimary}`}>${materialForm.purchase_price?.toFixed(2)}</span>
                            </div>
                            <div>
                              <span className={theme.textSecondary}>Cost per piece:</span>{' '}
                              <span className={`font-bold text-emerald-600 dark:text-emerald-400`}>${materialForm.cost_per_unit?.toFixed(4)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Enhanced Live Cost Calculations with Gradient Background */}
                  {materialForm.weight && materialForm.cost_per_unit > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-4 border-2 border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <span className={`text-sm font-semibold ${theme.textPrimary}`}>💡 Live Cost Per Weight Calculation</span>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                          <div className={`text-xs ${theme.textSecondary} mb-1`}>Per Gram</div>
                          <div className={`text-lg font-bold ${theme.textPrimary}`} data-testid="cost-per-gram">
                            ${(materialForm.cost_per_unit / (materialForm.weight * (materialForm.weight_unit === 'g' ? 1 : materialForm.weight_unit === 'kg' ? 1000 : materialForm.weight_unit === 'lb' ? 453.592 : 28.3495))).toFixed(4)}
                          </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                          <div className={`text-xs ${theme.textSecondary} mb-1`}>Per Kilogram</div>
                          <div className={`text-lg font-bold ${theme.textPrimary}`} data-testid="cost-per-kg">
                            ${(materialForm.cost_per_unit / (materialForm.weight * (materialForm.weight_unit === 'g' ? 0.001 : materialForm.weight_unit === 'kg' ? 1 : materialForm.weight_unit === 'lb' ? 0.453592 : 0.0283495))).toFixed(2)}
                          </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                          <div className={`text-xs ${theme.textSecondary} mb-1`}>Per Pound</div>
                          <div className={`text-lg font-bold ${theme.textPrimary}`} data-testid="cost-per-lb">
                            ${(materialForm.cost_per_unit / (materialForm.weight * (materialForm.weight_unit === 'g' ? 0.00220462 : materialForm.weight_unit === 'kg' ? 2.20462 : materialForm.weight_unit === 'lb' ? 1 : 0.0625))).toFixed(2)}
                          </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                          <div className={`text-xs ${theme.textSecondary} mb-1`}>Per Ounce</div>
                          <div className={`text-lg font-bold ${theme.textPrimary}`} data-testid="cost-per-oz">
                            ${(materialForm.cost_per_unit / (materialForm.weight * (materialForm.weight_unit === 'g' ? 0.035274 : materialForm.weight_unit === 'kg' ? 35.274 : materialForm.weight_unit === 'lb' ? 16 : 1))).toFixed(4)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 4: Supplier Information */}
              <div className={`${theme.isDark ? 'bg-orange-900/20 border-orange-700' : 'bg-orange-50 border-orange-200'} rounded-lg p-5 border`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
                    <span className="text-orange-600 dark:text-orange-400 font-bold text-sm">4</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <h4 className={`text-lg font-semibold ${theme.textPrimary}`}>Supplier Information</h4>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Supplier Name</label>
                    <input
                      type="text"
                      value={materialForm.supplier}
                      onChange={(e) => setMaterialForm({...materialForm, supplier: e.target.value})}
                      className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="Supplier company name"
                      data-testid="supplier-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Supplier SKU</label>
                    <input
                      type="text"
                      value={materialForm.supplier_sku}
                      onChange={(e) => setMaterialForm({...materialForm, supplier_sku: e.target.value})}
                      className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="SKU or product code"
                      data-testid="supplier-sku-input"
                    />
                  </div>
                </div>
              </div>

              {/* Section 5: Additional Details */}
              <div className={`${theme.isDark ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-5 border`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <span className={`text-gray-600 dark:text-gray-400 font-bold text-sm`}>5</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h4 className={`text-lg font-semibold ${theme.textPrimary}`}>Additional Details</h4>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Storage Location</label>
                      <input
                        type="text"
                        value={materialForm.location}
                        onChange={(e) => setMaterialForm({...materialForm, location: e.target.value})}
                        className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="e.g., Shelf A3, Room 2, Warehouse B"
                        data-testid="storage-location-input"
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                        Color <span className={`text-xs font-normal ${theme.textSecondary}`}>(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={materialForm.color || ''}
                        onChange={(e) => setMaterialForm({...materialForm, color: e.target.value})}
                        className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="e.g., Red, Blue, Black, Transparent"
                        data-testid="color-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Notes</label>
                    <textarea
                      value={materialForm.notes}
                      onChange={(e) => setMaterialForm({...materialForm, notes: e.target.value})}
                      className={`w-full px-4 py-3 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                      rows={3}
                      placeholder="Additional notes or special handling instructions..."
                      data-testid="notes-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sticky Footer Actions */}
            <div className={`sticky bottom-0 ${theme.card} border-t ${theme.border} px-6 py-4 flex justify-between items-center z-10`}>
              <div className={`text-sm ${theme.textSecondary}`}>
                <span className="text-red-500">*</span> Required fields
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAddMaterial(false);
                    setEditingMaterial(null);
                  }}
                  className={`px-6 py-3 rounded-lg ${theme.bgSecondary} ${theme.textPrimary} hover:opacity-80 transition-all font-medium`}
                  data-testid="cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveMaterial}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-medium shadow-lg flex items-center gap-2"
                  data-testid="create-material-button"
                >
                  {editingMaterial ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Update Material
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Material
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagementEnhanced;